import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import useThemeProps from "./useThemeProps.jsx";

const TableRoot = styled("div")`
  overflow-x: auto;
`;

const TableElement = styled("table")`
  border-collapse: collapse;
  min-width: 100%;
`;

function parseField(field) {
  if (typeof field === "string") {
    return { label: field };
  }
  return field;
}

function isTransparent(color) {
  return !color || color === "transparent";
}

export default function Table(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "Table" });
  let {
    items,
    fields,
    spacingInline = 4,
    spacingBlock = 4,
    borderColor,
    color,
    stripedColor,
    hoverColor,
    borderRadius = 0,
    ...restProps
  } = props;

  stripedColor =
    !isTransparent(theme.getColor(color)) &&
    theme.getColor([color, stripedColor]);
  let hasColor = !isTransparent(theme.getColor(color));

  if (!hasColor) {
    borderRadius = 0;
  }

  let striped = !!stripedColor;

  borderColor ??= striped ? null : "border";

  if (!fields) {
    fields = new Set(items.flatMap((item) => Object.keys(item)));
    fields = Array.from(fields).map((name) => ({ name }));
  } else if (!Array.isArray(fields)) {
    fields = Object.entries(fields).map(([name, field]) => ({
      name,
      ...parseField(field),
    }));
  }

  let renderHeaderRow = true;

  return (
    <TableRoot
      css={css`
        border-radius: ${theme.getLength(borderRadius)};
      `}
      {...restProps}
    >
      <TableElement
        css={css`
          border-radius: ${theme.getLength(borderRadius)};
          overflow: hidden;
        `}
      >
        {renderHeaderRow && (
          <thead>
            <tr
              css={css`
                background-color: ${theme.getColor(color)};
                &:hover {
                  background-color: ${hoverColor &&
                  theme.getColor([color, hoverColor])};
                }
              `}
            >
              {fields.map(({ name, label }, index) => (
                <th
                  key={name}
                  css={css`
                    border-bottom-width: 1px;
                    border-bottom-color: ${theme.getColor(borderColor)};
                    border-bottom-style: ${!theme.getColor(borderColor)
                      ? "solid"
                      : "none"};
                    font-weight: bold;
                    padding-inline-start: calc(
                      ${index > 0 || hasColor
                          ? theme.getLength(spacingInline)
                          : "0rem"} +
                        ${index === 0 ? theme.getLength(borderRadius) : "0rem"}
                    );
                    padding-inline-end: calc(
                      ${index < fields.length - 1 || hasColor
                          ? theme.getLength(spacingInline)
                          : "0rem"} +
                        ${index === fields.length - 1
                          ? theme.getLength(borderRadius)
                          : "0rem"}
                    );
                    padding-block-start: ${hasColor
                      ? theme.getLength(spacingBlock)
                      : null};
                    padding-block-end: ${theme.getLength(spacingBlock)};
                    text-align: start;
                  `}
                >
                  {label || name}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {items.map((item, rowIndex) => (
            <tr
              key={rowIndex}
              css={css`
                background-color: ${hasColor
                  ? rowIndex % 2 === 0 && striped
                    ? stripedColor
                    : theme.getColor(color)
                  : null};
                &:hover {
                  background-color: ${hoverColor &&
                  theme.getColor([
                    rowIndex % 2 === 0 && striped
                      ? stripedColor
                      : theme.getColor(color),
                    hoverColor,
                  ])};
                }
              `}
            >
              {fields.map(({ name }, index) => (
                <td
                  key={name}
                  css={css`
                    border-bottom-width: ${theme.getColor(borderColor)
                      ? 0
                      : "1px"};
                    border-bottom-color: ${theme.getColor(borderColor)};
                    border-bottom-style: ${!theme.getColor(borderColor) &&
                    rowIndex < items.length - 1
                      ? "solid"
                      : "none"};
                    padding-inline-start: calc(
                      ${index > 0 || hasColor
                          ? theme.getLength(spacingInline)
                          : "0rem"} +
                        ${index === 0 ? theme.getLength(borderRadius) : "0rem"}
                    );
                    padding-inline-end: calc(
                      ${index < fields.length - 1 || hasColor
                          ? theme.getLength(spacingInline)
                          : "0rem"} +
                        ${index === fields.length - 1
                          ? theme.getLength(borderRadius)
                          : "0rem"}
                    );
                    padding-block-start: ${renderHeaderRow || rowIndex > 0
                      ? theme.getLength(spacingBlock)
                      : null};
                    padding-block-end: ${rowIndex < items.length - 1 || hasColor
                      ? theme.getLength(spacingBlock)
                      : null};
                    text-align: start;
                  `}
                >
                  {item[name]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableElement>
    </TableRoot>
  );
}
